import type { Column as TableColumn } from "@replo/design-system/components/table/Table";
import type { Product } from "schemas/generated/product";

import * as React from "react";

import Input from "@editor/components/common/designSystem/Input";
import { openShopifyProductPageUrl } from "@editor/components/dashboard/products/utils";
import { useCurrentProjectContext } from "@editor/contexts/CurrentProjectContext";
import useCurrentProjectId from "@editor/hooks/useCurrentProjectId";
import { productRouter } from "@editor/trpc/product";
import { useAllowScroll } from "@editor/utils/hooks/useAllowScroll";
import { routes } from "@editor/utils/router";

import { buildReploProductTableColumns } from "@/features/products/components/ReploProductTableColumns";
import { buildShopifyProductTableColumns } from "@/features/products/components/ShopifyProductTableColumns";
import Button from "@replo/design-system/components/button/Button";
import IconButton from "@replo/design-system/components/button/IconButton";
import EmptyState from "@replo/design-system/components/empty-state/EmptyState";
import { Menu } from "@replo/design-system/components/menu/Menu";
import PageHeader from "@replo/design-system/components/PageHeader";
import { useIsMobile } from "@replo/design-system/components/shadcn/hooks/use-mobile";
import { Skeleton } from "@replo/design-system/components/skeleton/Skeleton";
import Table from "@replo/design-system/components/table/Table";
import twMerge from "@replo/design-system/utils/twMerge";
import { skipToken } from "@tanstack/react-query";
import {
  ExternalLink,
  MoreHorizontal,
  Package,
  Plus,
  Search,
} from "lucide-react";
import { generatePath, useNavigate } from "react-router-dom";
import { useDebouncedState } from "replo-utils/react/use-debounced-state";

const ProductMenu: React.FC<{ onEditProduct: () => void }> = ({
  onEditProduct,
}) => {
  return (
    <Menu
      items={[
        {
          id: "edit",
          type: "leaf",
          variant: "default",
          size: "sm",
          title: "Edit Product",
          onSelect: onEditProduct,
        },
      ]}
      align="end"
      trigger={
        <IconButton
          variant="tertiary"
          size="sm"
          icon={<MoreHorizontal size={16} className="text-muted" />}
        />
      }
    />
  );
};

const PAGE_SIZE = 20;

type ProductTableRow =
  | (Product & { key: string; isLoading?: false })
  | { key: string; isLoading: true };

export function ProductsTable() {
  const currentProjectId = useCurrentProjectId();
  const navigate = useNavigate();
  const { project } = useCurrentProjectContext();
  const isMobile = useIsMobile();

  const shopifyData = project?.integrations?.shopify;
  const shopifyStore = shopifyData?.store;

  const scrollContainerRef = React.useRef<HTMLDivElement>(null);

  const [searchQuery, debouncedSearchQuery, setSearchQuery] = useDebouncedState(
    "",
    500,
  );

  const {
    data: semanticSearchData,
    isLoading: isLoadingSemanticSearch,
    fetchNextPage: fetchNextSemanticPage,
    isFetching: isFetchingSemantic,
    hasNextPage: hasNextSemanticPage,
  } = productRouter.find.useInfiniteQuery(
    currentProjectId && debouncedSearchQuery
      ? {
          config: {
            projectId: currentProjectId,
            filters: {
              searchTerm: debouncedSearchQuery,
              semantic: true,
            },
          },
          pageSize: PAGE_SIZE,
        }
      : skipToken,
    {
      enabled: Boolean(currentProjectId && debouncedSearchQuery),
      staleTime: 0,
      getNextPageParam: ({ nextCursor }) => nextCursor,
    },
  );

  const {
    data: listData,
    isLoading: isLoadingList,
    fetchNextPage: fetchNextListPage,
    isFetching: isFetchingList,
    hasNextPage: hasNextListPage,
  } = productRouter.list.useInfiniteQuery(
    currentProjectId && !debouncedSearchQuery
      ? {
          projectId: currentProjectId,
          // Note (Noah, 2025-09-08): This is always true for products dashboard, since if
          // the user got here they're in a v2 project and thus have updated their scopes
          writeThroughProductsSync: true,
          filters: {
            searchTerm: debouncedSearchQuery,
          },
          limit: PAGE_SIZE,
        }
      : skipToken,
    {
      getNextPageParam: (lastPage) => lastPage.nextCursor,
    },
  );

  const data = debouncedSearchQuery ? semanticSearchData : listData;
  const isLoadingProducts = debouncedSearchQuery
    ? isLoadingSemanticSearch
    : isLoadingList;
  const fetchNextPage = debouncedSearchQuery
    ? fetchNextSemanticPage
    : fetchNextListPage;
  const isFetching = debouncedSearchQuery ? isFetchingSemantic : isFetchingList;
  const hasNextPage = debouncedSearchQuery
    ? hasNextSemanticPage ?? false
    : hasNextListPage ?? false;

  const allProducts = React.useMemo(() => {
    return data?.pages.flatMap((page) => page.products) ?? [];
  }, [data]);

  const totalCount = React.useMemo(() => {
    if (debouncedSearchQuery) {
      return allProducts.length;
    }
    return listData?.pages[0]?.totalCount ?? 0;
  }, [debouncedSearchQuery, allProducts.length, listData]);

  const tableData = React.useMemo<ProductTableRow[]>(() => {
    const products: ProductTableRow[] = allProducts.map((product) => ({
      ...product,
      key: product.id,
      isLoading: false,
    }));

    if (isFetching && !isLoadingProducts && allProducts.length > 0) {
      products.push({
        key: "loading-row",
        isLoading: true,
      });
    }

    return products;
  }, [allProducts, isFetching, isLoadingProducts]);

  const fetchMoreOnBottomReached = React.useCallback(
    (containerRefElement?: HTMLDivElement | null) => {
      if (containerRefElement) {
        const { scrollHeight, scrollTop, clientHeight } = containerRefElement;
        if (
          scrollHeight - scrollTop - clientHeight < 100 &&
          !isFetching &&
          hasNextPage
        ) {
          void fetchNextPage();
        }
      }
    },
    [fetchNextPage, isFetching, hasNextPage],
  );

  React.useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) {
      return;
    }

    const handleScroll = () => {
      fetchMoreOnBottomReached(container);
    };

    fetchMoreOnBottomReached(container);

    container.addEventListener("scroll", handleScroll);
    return () => {
      container.removeEventListener("scroll", handleScroll);
    };
  }, [fetchMoreOnBottomReached]);

  useAllowScroll();

  const onProductClick = React.useCallback(
    (clickedProductId: string) => {
      if (currentProjectId) {
        const path = generatePath(routes.shops.products.edit, {
          projectId: currentProjectId,
          productId: clickedProductId,
        });
        navigate(path);
      }
    },
    [currentProjectId, navigate],
  );

  const onEditInShopify = React.useCallback(() => {
    if (shopifyStore?.shopifyUrl) {
      openShopifyProductPageUrl(shopifyStore.shopifyUrl);
    }
  }, [shopifyStore?.shopifyUrl]);

  const onNewProduct = React.useCallback(() => {
    navigate(
      generatePath(routes.shops.products.new, {
        projectId: currentProjectId,
      }),
    );
  }, [currentProjectId, navigate]);

  const tableColumns = React.useMemo<TableColumn<ProductTableRow>[]>(() => {
    const baseColumns = shopifyData
      ? buildShopifyProductTableColumns()
      : buildReploProductTableColumns();

    const columnsWithLoadingState = baseColumns.map((col) => ({
      ...col,
      renderCell: (row: ProductTableRow) => {
        if (row.isLoading) {
          return <Skeleton className="h-4 w-full" />;
        }
        if (col.renderCell) {
          return col.renderCell(row);
        }
        return row[col.key as keyof Product] ?? "N/A";
      },
    })) as TableColumn<ProductTableRow>[];

    if (shopifyData) {
      return columnsWithLoadingState;
    }

    const actionsColumn: TableColumn<ProductTableRow> = {
      key: "actions",
      header: null,
      width: "80px",
      align: "right",
      renderCell: (product) => {
        if (product.isLoading) {
          return <Skeleton className="h-4 w-full" />;
        }
        return <ProductMenu onEditProduct={() => onProductClick(product.id)} />;
      },
    };

    return [...columnsWithLoadingState, actionsColumn];
  }, [shopifyData, onProductClick]);

  const headerDescription = React.useMemo(() => {
    if (isLoadingProducts) {
      return <Skeleton className="h-4 w-48" />;
    }

    if (shopifyData) {
      return `${totalCount} ${totalCount === 1 ? "product" : "products"} synced from Shopify`;
    }

    return `${totalCount} Replo ${totalCount === 1 ? "product" : "products"}`;
  }, [isLoadingProducts, shopifyData, totalCount]);

  return (
    <>
      <PageHeader
        title="Products"
        description={headerDescription}
        actions={
          <div className="flex gap-2">
            {shopifyData ? (
              <Button
                variant="secondary"
                size="base"
                endEnhancer={<ExternalLink size={14} />}
                onClick={onEditInShopify}
              >
                Edit in Shopify
              </Button>
            ) : (
              <Button
                variant="primary"
                size="base"
                startEnhancer={<Plus size={14} />}
                onClick={onNewProduct}
              >
                New Product
              </Button>
            )}
          </div>
        }
      />
      <div
        className={twMerge(
          "flex w-full flex-col gap-y-6 py-6",
          isMobile && "px-4",
        )}
      >
        <Input
          placeholder="Search products..."
          startEnhancer={<Search size={14} />}
          layoutClassName="w-full"
          size="base"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        <Table<ProductTableRow>
          data={tableData}
          columns={tableColumns}
          isLoading={isLoadingProducts}
          loadingRows={20}
          emptyState={
            <EmptyState
              icon={<Package size={48} />}
              title="No products found"
              description={
                searchQuery
                  ? "Try adjusting your search."
                  : "No products available."
              }
            >
              <Button
                variant="secondary"
                size="sm"
                endEnhancer={<ExternalLink size={14} />}
                onClick={onEditInShopify}
              >
                Edit in Shopify
              </Button>
            </EmptyState>
          }
          onRowClick={
            shopifyData
              ? undefined
              : (product) => {
                  if (!product.isLoading) {
                    onProductClick(product.id);
                  }
                }
          }
          getRowClassName={(row) =>
            row.isLoading ? "hover:bg-transparent cursor-default" : undefined
          }
          scrollable={true}
          variant="bordered"
          maxBodyHeight={`calc(100vh - ${isMobile ? "285px" : "260px"})`}
          size="sm"
          scrollContainerRef={scrollContainerRef}
        />
      </div>
    </>
  );
}
